<script setup>
import { computed, ref, toRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStageTimer } from 'dashboard/composables/kanban/useStageTimer';
import {
  useKanbanCard,
  getChannelColorClass,
} from 'dashboard/composables/kanban/useKanbanCard';

const props = defineProps({
  conversation: {
    type: Object,
    required: true,
  },
  stageConfig: {
    type: Object,
    required: true,
  },
  mapping: {
    type: Object,
    default: () => ({}),
  },
  funnelId: {
    type: Number,
    required: false,
    default: null,
  },
  stageName: {
    type: String,
    required: true,
  },
});

const emit = defineEmits([
  'click',
  'open-conversation',
  'open-crm',
  'open-side-panel',
  'drag-start',
  'remove-from-funnel',
]);

const { t } = useI18n();

// Estado para controlar hover
const isHovered = ref(false);

// Use composable para todas as computed properties
const conversationRef = toRef(props, 'conversation');
const mappingRef = toRef(props, 'mapping');
const funnelIdRef = toRef(props, 'funnelId');

const {
  currentContact,
  inbox,
  inboxChannelIcon,
  assignedAgents,
  visibleAgents,
  additionalAgentsCount,
  activeLabels,
  statusColor,
  statusLabel,
  totalValue,
  appointmentDateTime,
  messagePreview,
} = useKanbanCard(conversationRef, mappingRef, funnelIdRef);

// Timer computations
const { compactTime, getSLAClass, isSLABreached } = useStageTimer(
  props.mapping?.stage_entered_at || props.conversation.stage_entered_at,
  props.mapping?.timer_data || {}
);

// Verificar se tem SLA violado
const hasSLAViolation = computed(() =>
  isSLABreached(props.stageConfig.sla_hours)
);

// Cor do estágio
const stageColor = computed(() => props.stageConfig?.color || '#6366f1');

// Estilo de glow baseado na cor do estágio
const cardGlowStyle = computed(() => {
  const color = stageColor.value;
  return {
    '--stage-color': color,
    '--stage-color-glow': `${color}20`,
  };
});

// Formatar valor total de forma compacta
const formattedTotalValue = computed(() => {
  if (!totalValue.value) return null;
  const value = parseFloat(totalValue.value);
  if (value >= 1000) {
    return `R$ ${(value / 1000).toFixed(1)}k`;
  }
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
});

// Formatar agendamento de forma compacta
const compactAppointment = computed(() => {
  if (!appointmentDateTime.value || appointmentDateTime.value === '--')
    return null;
  return appointmentDateTime.value;
});

// Event handlers
const handleCardClick = () => {
  // Emite evento para abrir o painel lateral
  emit('click', props.conversation);
};

const handleOpenConversation = event => {
  event.stopPropagation();
  emit('open-conversation', props.conversation);
};

const handleOpenCRM = event => {
  event.stopPropagation();
  emit('open-crm', props.conversation, currentContact.value);
};

const handleRemoveFromFunnel = event => {
  event.stopPropagation();
  const funnelName = props.conversation.funnel_name || 'este funil';

  if (
    window.confirm(
      `Remover do Funil\n\nTem certeza que deseja remover esta conversa do funil "${funnelName}"?`
    )
  ) {
    emit('remove-from-funnel', props.conversation);
  }
};

const handleDragStart = event => {
  event.dataTransfer.effectAllowed = 'move';

  const dragData = {
    conversationId: props.conversation.id,
    fromColumn: props.stageName,
  };

  event.dataTransfer.setData('application/json', JSON.stringify(dragData));
  event.dataTransfer.setData(
    'conversationId',
    props.conversation.id.toString()
  );
  event.dataTransfer.setData(
    'conversationData',
    JSON.stringify(props.conversation)
  );

  event.target.classList.add('dragging');
  emit('drag-start', props.conversation.id, props.stageName, event);
};

const handleDragEnd = event => {
  event.target.classList.remove('dragging');
};
</script>

<template>
  <div
    class="kanban-card bg-n-solid-3 rounded-xl border border-n-weak cursor-pointer transition-all duration-200 hover:border-n-slate-8 hover:shadow-lg hover:shadow-n-black/20 overflow-hidden relative"
    :class="{
      'ring-2 ring-n-ruby-9/50': hasSLAViolation,
    }"
    :style="cardGlowStyle"
    draggable="true"
    @click="handleCardClick"
    @dragstart="handleDragStart"
    @dragend="handleDragEnd"
    @mouseenter="isHovered = true"
    @mouseleave="isHovered = false"
  >
    <!-- Barra lateral colorida do estágio -->
    <div
      class="absolute left-0 top-0 bottom-0 w-1 rounded-l-xl transition-all duration-200"
      :style="{ backgroundColor: stageColor }"
    />

    <!-- Header: Inbox + Timer/SLA -->
    <div
      class="flex items-center justify-between px-3 py-2 border-b border-n-weak ml-1"
    >
      <div class="flex items-center gap-1.5">
        <span class="text-xs font-medium text-n-slate-11">
          {{ inbox?.name || 'Inbox' }}
        </span>
      </div>
      <div class="flex items-center gap-2">
        <!-- Timer -->
        <span
          v-if="compactTime"
          class="text-xs font-medium"
          :class="hasSLAViolation ? 'text-n-ruby-11' : 'text-n-amber-11'"
        >
          {{ compactTime }}
          <span v-if="hasSLAViolation" class="ml-0.5 text-n-ruby-11">SLA!</span>
        </span>
        <!-- Botão Remover -->
        <button
          v-if="isHovered"
          class="p-1 text-n-slate-10 hover:text-n-ruby-11 rounded transition-colors"
          title="Remover do funil"
          @click="handleRemoveFromFunnel"
        >
          <fluent-icon icon="delete" size="14" />
        </button>
      </div>
    </div>

    <!-- Conteúdo Principal -->
    <div class="p-3 ml-1">
      <!-- Linha 1: Avatar + Nome + Botões -->
      <div class="flex items-start gap-3">
        <!-- Avatar do Contato -->
        <div class="flex-shrink-0">
          <img
            v-if="currentContact?.thumbnail"
            :src="currentContact.thumbnail"
            :alt="currentContact.name"
            class="w-10 h-10 rounded-full object-cover"
          />
          <div
            v-else
            class="w-10 h-10 rounded-full bg-woot-500 text-white text-sm flex items-center justify-center font-medium"
          >
            {{ currentContact?.name?.charAt(0) || 'U' }}
          </div>
          <!-- Labels do contato (emojis) -->
          <div
            v-if="activeLabels.length > 0"
            class="flex items-center gap-0.5 mt-1 justify-center"
          >
            <span
              v-for="label in activeLabels.slice(0, 2)"
              :key="label.id"
              class="text-xs"
              :title="label.title"
            >
              {{ label.title?.match(/[\u{1F300}-\u{1F9FF}]/u)?.[0] || '🏷️' }}
            </span>
          </div>
        </div>

        <!-- Info do Contato -->
        <div class="flex-1 min-w-0">
          <p class="text-sm font-semibold text-n-slate-12 truncate">
            {{ currentContact?.name || 'Usuário' }}
          </p>
          <!-- Agente atribuído -->
          <div
            v-if="assignedAgents.length > 0"
            class="flex items-center gap-1 mt-1"
          >
            <img
              v-if="visibleAgents[0]?.avatar_url"
              :src="visibleAgents[0].avatar_url"
              :alt="visibleAgents[0].name"
              class="w-4 h-4 rounded-full object-cover"
            />
            <span class="text-xs text-n-slate-10">{{
              visibleAgents[0]?.name
            }}</span>
          </div>
        </div>
      </div>

      <!-- Mensagem Preview -->
      <div
        class="mt-3 px-3 py-2 bg-n-alpha-1 border border-n-weak rounded-lg"
      >
        <p class="text-xs text-n-slate-11 line-clamp-2">
          {{ messagePreview || 'Sem mensagens ainda' }}
        </p>
      </div>

      <!-- Boxes: Valor + Agendamento -->
      <div class="grid grid-cols-2 gap-2 mt-3">
        <!-- Valor Total -->
        <div
          class="px-3 py-2 bg-n-alpha-1 border border-n-weak rounded-lg"
        >
          <p
            class="text-[10px] font-medium text-n-slate-10 uppercase tracking-wide"
          >
            Valor Total
          </p>
          <p class="text-sm font-bold text-n-teal-11 mt-0.5">
            {{ formattedTotalValue || 'R$ 0,00' }}
          </p>
        </div>
        <!-- Agendamento -->
        <div
          class="px-3 py-2 bg-n-alpha-1 border border-n-weak rounded-lg"
        >
          <p
            class="text-[10px] font-medium text-n-slate-10 uppercase tracking-wide"
          >
            Agendamento
          </p>
          <p class="text-sm font-bold text-n-slate-12 mt-0.5">
            {{ compactAppointment || '--' }}
          </p>
        </div>
      </div>

      <!-- Footer: Tags + Status -->
      <div
        class="flex items-center justify-between mt-3 pt-2 px-2 py-1.5 -mx-3 -mb-3 bg-n-alpha-1 border-t border-n-weak"
      >
        <!-- Tags -->
        <div class="flex items-center gap-1 flex-wrap max-w-[65%]">
          <span
            v-for="label in activeLabels.slice(0, 3)"
            :key="label.id"
            class="inline-flex items-center text-[10px] px-1.5 py-0.5 rounded font-medium"
            :style="{
              backgroundColor: label.color + '30',
              color: label.color,
            }"
          >
            {{ label.title }}
          </span>
          <span
            v-if="activeLabels.length === 0"
            class="text-[10px] text-n-slate-10 px-1.5 py-0.5 bg-n-alpha-2 rounded"
          >
            Sem tags
          </span>
          <span
            v-else-if="activeLabels.length > 3"
            class="text-[10px] text-n-slate-10 px-1.5 py-0.5 bg-n-alpha-2 rounded"
          >
            +{{ activeLabels.length - 3 }}
          </span>
        </div>

        <!-- Status Badge -->
        <span
          class="text-[10px] px-2 py-1 rounded font-semibold uppercase tracking-wide flex-shrink-0"
          :class="[statusColor]"
        >
          {{ statusLabel }}
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.kanban-card {
  min-width: 100%;
  --stage-color: #6366f1;
  --stage-color-glow: rgba(99, 102, 241, 0.12);
}

.kanban-card.dragging {
  opacity: 0.5;
  transform: rotate(2deg);
}

.kanban-card:hover {
  transform: translateY(-2px);
  box-shadow:
    0 10px 25px -5px var(--stage-color-glow),
    0 8px 10px -6px var(--stage-color-glow),
    0 0 0 1px var(--stage-color);
  border-color: var(--stage-color) !important;
}

/* Efeito de brilho sutil no hover */
.kanban-card::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  opacity: 0;
  transition: opacity 0.2s ease;
  background: linear-gradient(
    135deg,
    var(--stage-color-glow) 0%,
    transparent 50%,
    transparent 100%
  );
  pointer-events: none;
  z-index: 0;
}

.kanban-card:hover::before {
  opacity: 1;
}

/* Garantir que o conteúdo fique acima do efeito */
.kanban-card > * {
  position: relative;
  z-index: 1;
}
</style>
